import { decodeLeaderboardEntry } from '../leaderboard/decoder';
import { sortLeaderboardEntries } from '../leaderboard/ranking';
import { SEASON_1_ARCHIVE_PATH, SEASON_2_ARCHIVE_PATH } from '../leaderboard/seasons';
import type { LeaderboardEntry, RankedLeaderboardEntry } from '../leaderboard/types';
import { decodeReplay } from '../replay/decoder';
import { LEGACY_V1_LINE_DECODER } from '../replay/legacy-v1-lines';
import type { FreeSpinReplayV2, LegacyReplayLineDecoder } from '../replay/schema';

export type ArchivedSeasonId = 'season1' | 'season2';

export interface SeasonArchive {
  readonly id: ArchivedSeasonId;
  readonly label: string;
  readonly runs: readonly RankedLeaderboardEntry[];
  readonly freeSpins: readonly RankedLeaderboardEntry[];
  readonly replays: ReadonlyMap<string, FreeSpinReplayV2>;
}

/** Kept as a source-compatible name for existing integrations. */
export type SeasonOneArchive = SeasonArchive;

export interface ArchiveFetchPort {
  fetchJson(path: string): Promise<unknown>;
}

export class BrowserArchiveFetch implements ArchiveFetchPort {
  async fetchJson(path: string): Promise<unknown> {
    const response = await fetch(path, { cache: 'force-cache' });
    if (!response.ok) throw new Error(`Season archive request failed (${response.status}).`);
    return response.json() as Promise<unknown>;
  }
}

type UnknownRecord = Record<string, unknown>;
function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

const ARCHIVE_PATHS: Readonly<Record<ArchivedSeasonId, string>> = Object.freeze({
  season1: SEASON_1_ARCHIVE_PATH,
  season2: SEASON_2_ARCHIVE_PATH,
});

export class SeasonArchiveService {
  private readonly cache = new Map<ArchivedSeasonId, SeasonArchive>();

  constructor(
    private readonly source: ArchiveFetchPort = new BrowserArchiveFetch(),
    private readonly legacyLineDecoder: LegacyReplayLineDecoder = LEGACY_V1_LINE_DECODER,
  ) {}

  loadSeasonOne(): Promise<SeasonArchive> {
    return this.load('season1');
  }

  loadSeasonTwo(): Promise<SeasonArchive> {
    return this.load('season2');
  }

  async load(id: ArchivedSeasonId): Promise<SeasonArchive> {
    const cached = this.cache.get(id);
    if (cached) return cached;
    const payload = record(await this.source.fetchJson(ARCHIVE_PATHS[id]));
    const runs = record(payload?.runs);
    const freeSpins = record(payload?.freeSpins);
    if (payload?.id !== id || typeof payload.label !== 'string' || !Array.isArray(runs?.entries)
      || !Array.isArray(freeSpins?.entries)) throw new TypeError(`${id} archive has an invalid structure.`);
    const decodeEntries = (values: readonly unknown[], board: 'runs' | 'freeSpins'): LeaderboardEntry[] => values
      .map((value) => decodeLeaderboardEntry(value, { board, seasonId: id }))
      .filter((entry): entry is LeaderboardEntry => entry !== null);
    const replayMap = new Map<string, FreeSpinReplayV2>();
    const rawReplays = record(freeSpins.replays);
    if (rawReplays) {
      for (const [entryId, value] of Object.entries(rawReplays)) {
        const replay = decodeReplay(value, entryId, this.legacyLineDecoder);
        if (replay) replayMap.set(entryId, replay);
      }
    }
    const archive = Object.freeze({
      id,
      label: payload.label,
      runs: sortLeaderboardEntries(decodeEntries(runs.entries, 'runs'), 10),
      freeSpins: sortLeaderboardEntries(decodeEntries(freeSpins.entries, 'freeSpins'), 10),
      replays: replayMap,
    });
    this.cache.set(id, archive);
    return archive;
  }
}
